import Papa from "papaparse"
import { bookingWithoutExtensions, bookingsByDayObject, dayOfPayouts, getConfirmedExtensions } from "../../Helpers/Bookings/bookingHelpers"
import { PropertyBookingEntry } from "../../Types/bookingTypes"
import timeService, { DATE_FORMAT } from "../../services/time/timeService"
import { currencyFormatter } from "../../Helpers/Parsers/currencyFormatter"
import { FieldGeneratorFieldType } from "../../Components/FormFields/FieldGenerator/FieldGenerator"

export const dateHasher = (startDate: any, endDate: any, options: any = {}) => {
    return timeService.createDateHashObject(
        startDate,
        endDate,
        options
    )
}

const buildMonthlyRangeData = (startDate: number, endDate: number) => {
    const rangeStart = timeService.createLuxonDate(new Date(startDate)).startOf('day')
    const rangeEnd = timeService.createLuxonDate(new Date(endDate)).startOf('day')
    const rangeEndExclusive = rangeEnd.plus({ days: 1 })
    const today = timeService.createLuxonDate(new Date()).startOf('day')

    const dateRangeHash: any = {}
    const dateRangeDailyHash: any = {}
    const dateRangeMonthYears: string[] = []
    const dateRangeDaysByMonthYear: any = {}
    const futureDaysByMonthYear: any = {}

    let currentMonth = rangeStart.startOf('month')
    const lastMonth = rangeEnd.startOf('month')

    while (currentMonth <= lastMonth) {
        const monthYear = `${currentMonth.toFormat('yyyy')}-${currentMonth.toFormat('MM')}`
        dateRangeMonthYears.push(monthYear)
        dateRangeHash[monthYear] = { date: monthYear }

        const monthStart = currentMonth
        const nextMonth = currentMonth.plus({ months: 1 })
        const segmentStart = monthStart < rangeStart ? rangeStart : monthStart
        const segmentEnd = nextMonth < rangeEndExclusive ? nextMonth : rangeEndExclusive
        const segmentDays = Math.max(0, segmentEnd.diff(segmentStart, 'days').days)
        dateRangeDaysByMonthYear[monthYear] = segmentDays

        const futureSegmentStart = today.plus({ days: 1 }) > segmentStart ? today.plus({ days: 1 }) : segmentStart
        const futureSegmentEnd = segmentEnd
        futureDaysByMonthYear[monthYear] = futureSegmentStart < futureSegmentEnd
            ? Math.max(0, futureSegmentEnd.diff(futureSegmentStart, 'days').days)
            : 0

        currentMonth = nextMonth
    }

    let dayCursor = rangeStart
    while (dayCursor < rangeEndExclusive) {
        dateRangeDailyHash[dayCursor.toFormat(DATE_FORMAT)] = true
        dayCursor = dayCursor.plus({ days: 1 })
    }

    return {
        rangeStart,
        rangeEndExclusive,
        dateRangeHash,
        dateRangeDailyHash,
        dateRangeMonthYears,
        dateRangeDaysByMonthYear,
        futureDaysByMonthYear,
    }
}

const isMaintenanceOrBlockedBooking = (booking: any) =>
    booking.source === 'repairs' || booking.source === 'blocked'

export const generateCSVURL = (
    myProperties: any,
    propertyReportData: any,
    propertyTotalsReport: any,
) => {
    const csvData: any[] = []
    propertyReportData.forEach((p: any) => {
        const csvEntry: any = {
            name: p.shortTitle,
            beds: myProperties[p.propertyId].about.bedrooms,
            baths: myProperties[p.propertyId].about.bathrooms,
            ...myProperties[p.propertyId].address,
        }

        Object.keys(p.monthlySummary).forEach((d: string) => {
            const monthStart = timeService.createLuxonDate(`${d}-01`)
            const monthEnd = monthStart.endOf('month').endOf('day').toMillis()
            const propertyCreated = myProperties && myProperties[p.propertyId] ? new Date(myProperties[p.propertyId].created).getTime() : 0

            if (propertyCreated > monthEnd) {
                csvEntry[`${d} ADR`] = 'N/A'
                csvEntry[`${d} Occupancy`] = 'N/A'
                csvEntry[`${d} RevPAR`] = 'N/A'
                csvEntry[`${d} Lead`] = 'N/A'
                csvEntry[`${d} Earnings`] = 'N/A'
                csvEntry[`${d} Payout`] = 'N/A'
                csvEntry[`${d} Guests`] = 'N/A'
                return
            }

            const {
                earnings,
                adr,
                occupancy,
                leadTime,
                guests,
                revpar,
            } = p.monthlySummary[d]
            csvEntry[`${d} ADR`] = adr
            csvEntry[`${d} Occupancy`] = occupancy
            csvEntry[`${d} RevPAR`] = revpar
            
            csvEntry[`${d} Lead`] = leadTime
            csvEntry[`${d} Earnings`] = earnings
            csvEntry[`${d} Payout`] = p.mappedData[d].payout
            csvEntry[`${d} Guests`] = guests
        })
        csvEntry['Total ADR'] = p.totals.adr
        csvEntry['Total Occupancy'] = p.totals.occupancy
        csvEntry['Total RevPAR'] = p.totals.revpar
        csvEntry['Total Lead'] = p.totals.leadTime
        csvEntry['Total Earnings'] = p.totals.earnings
        csvEntry['Total Payout'] = p.totals.payout
        csvEntry['Total Guests'] = p.totals.guests
        csvData.push(csvEntry)
    })
    const {
        totalsReport,
        totalsByMonth,
    } = propertyTotalsReport

    const csvTotalsEntry: any = {
        name: 'Totals',
    }

    Object.keys(totalsByMonth).forEach((d: string) => {
        const {
            earnings,
            adr,
            occupancy,
            leadTime,
            guests,
            revpar,
        } = totalsByMonth[d]
        csvTotalsEntry[`${d} ADR`] = adr
        csvTotalsEntry[`${d} Occupancy`] = occupancy
        csvTotalsEntry[`${d} RevPAR`] = revpar
        
        csvTotalsEntry[`${d} Lead`] = leadTime
        csvTotalsEntry[`${d} Earnings`] = earnings
        csvTotalsEntry[`${d} Payout`] = totalsByMonth[d].payout
        csvTotalsEntry[`${d} Guests`] = guests
    })

    csvTotalsEntry['Total ADR'] = totalsReport.adr
    csvTotalsEntry['Total Occupancy'] = totalsReport.occupancy
    csvTotalsEntry['Total RevPAR'] = totalsReport.revpar
    csvTotalsEntry['Total Lead'] = totalsReport.leadTime
    csvTotalsEntry['Total Earnings'] = totalsReport.earnings
    csvTotalsEntry['Total Payout'] = totalsReport.payout
    csvTotalsEntry['Total Guests'] = totalsReport.guests

    csvData.push(csvTotalsEntry)

    const csvString = Papa.unparse(csvData)
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    return url
}

export const generatePropertyReportData = (
    myProperties: any,
    selectedProperties: any,
    bookingsByProperty: any[],
    startDate: number,
    endDate: number,
) => {
    const {
        rangeStart,
        rangeEndExclusive,
        dateRangeHash,
        dateRangeDailyHash,
        dateRangeMonthYears,
        dateRangeDaysByMonthYear,
        futureDaysByMonthYear,
    } = buildMonthlyRangeData(startDate, endDate)

    const todayKey = timeService.getFormattedTime(new Date(), DATE_FORMAT)
    const propertyReport: any[] = []

    const foundProperties: any = {}
    const bookingByPropertyList = bookingsByProperty.map((b: any) => {
        const { propertyId } = b;
        foundProperties[propertyId] = true
        return b
    })
    if (selectedProperties) {
        Object.keys(selectedProperties).forEach((propertyId: string) => {
            if (!foundProperties[propertyId]) {
                bookingByPropertyList.push({
                    propertyId,
                    bookings: []
                })
            }
        })
    }

    bookingByPropertyList.forEach((b: { propertyId: string, bookings: PropertyBookingEntry[] }) => {
        const {
            propertyId,
            bookings,
        } = b;
        const mappedData: any = {}
        const uniqueBookingKeys: any = {}
        const bookingsByDay = bookingsByDayObject(bookings.filter((booking) => !isMaintenanceOrBlockedBooking(booking)))
        const activeBookingKeysByMonth: any = {}
        const activeBookingKeysInRange: any = {}
        const futureBookedDaysByMonthYear: any = {}
        const dailyBookings: any = {}
        const propertyOverlaps: any[] = []
        let activeBookingsInRange = 0

        dateRangeMonthYears.forEach((monthYear: string) => {
            mappedData[monthYear] = emptyReportNumbers()
            mappedData[monthYear].days = dateRangeDaysByMonthYear[monthYear] || 0
            mappedData[monthYear].nightsRemaining = futureDaysByMonthYear[monthYear] || 0
        })

        bookings.forEach((booking: any, bookingIndex: number) => {
            const isMaintenanceOrBlocked = isMaintenanceOrBlockedBooking(booking)
            const bookingStart = timeService.createLuxonDate(new Date(booking.checkIn)).startOf('day')
            const bookingEnd = timeService.createLuxonDate(new Date(booking.checkOut)).startOf('day')
            const rangeStartDate = bookingStart < rangeStart ? rangeStart : bookingStart
            const rangeEndDate = bookingEnd < rangeEndExclusive ? bookingEnd : rangeEndExclusive

            if (!isMaintenanceOrBlocked) {
                getConfirmedExtensions(booking).forEach((extension: any) => {
                    const extensionStartDateKey = timeService.createLuxonDate(new Date(extension.originalCheckOut || booking.checkOut)).toFormat(DATE_FORMAT)
                    if (!dateRangeDailyHash[extensionStartDateKey]) {
                        return
                    }

                    const payoutMonthYear = `${extensionStartDateKey.slice(0, 4)}-${extensionStartDateKey.slice(5, 7)}`
                    mappedData[payoutMonthYear].payout += extension.payout
                })
            }

            if (rangeStartDate >= rangeEndDate) {
                return
            }

            if (!isMaintenanceOrBlocked) {
                const bookingKey = booking._id || `${booking.checkIn}-${booking.checkOut}-${bookingIndex}`
                if (!activeBookingKeysInRange[bookingKey]) {
                    activeBookingKeysInRange[bookingKey] = true
                    activeBookingsInRange++
                }
            }

            const nights = timeService.calculateNights(booking.checkIn, booking.checkOut)
            const leadTime = Math.max(0, Math.floor(timeService.timeDiff(booking.dateBooked, booking.checkIn) || 0))
            const bookingCheckInKey = timeService.getFormattedTime(new Date(booking.checkIn), DATE_FORMAT)
            const bookingCheckInMonth = timeService.createLuxonDate(new Date(booking.checkIn)).toFormat('yyyy-MM')
            const earnings = booking.earnings || 0
            const payoutEarnings = bookingWithoutExtensions(booking).earnings || 0
            const pricePerNight = earnings / nights

            let dayCursor = rangeStartDate
            while (dayCursor < rangeEndDate) {
                const dayKey = dayCursor.toFormat(DATE_FORMAT)
                if (!dateRangeDailyHash[dayKey]) {
                    dayCursor = dayCursor.plus({ days: 1 })
                    continue
                }

                const monthYear = `${dayCursor.toFormat('yyyy')}-${dayCursor.toFormat('MM')}`
                const bucket = mappedData[monthYear]

                bucket.trueOccupancySleeps++
                if (isMaintenanceOrBlocked) {
                    dayCursor = dayCursor.plus({ days: 1 })
                    continue
                }

                const bookingKey = booking._id || `${booking.checkIn}-${booking.checkOut}-${bookingIndex}`
                activeBookingKeysByMonth[monthYear] = activeBookingKeysByMonth[monthYear] || {}
                if (!activeBookingKeysByMonth[monthYear][bookingKey]) {
                    activeBookingKeysByMonth[monthYear][bookingKey] = true
                    bucket.activeBookings++
                }
                bucket.nights += pricePerNight ? 1 : 0
                bucket.sleeps += 1
                bucket.earnings += pricePerNight

                dailyBookings[dayKey] = dailyBookings[dayKey] || []
                dailyBookings[dayKey].push(booking)

                if (dayKey > todayKey) {
                    futureBookedDaysByMonthYear[monthYear] = futureBookedDaysByMonthYear[monthYear] || {}
                    futureBookedDaysByMonthYear[monthYear][dayKey] = true
                }

                if (monthYear === bookingCheckInMonth && booking._id && !uniqueBookingKeys[booking._id] && booking.dateBooked && leadTime > -1) {
                    uniqueBookingKeys[booking._id] = true
                    bucket.guests += +(booking?.guestInfo?.guestTotal ? booking.guestInfo.guestTotal : 0)
                    bucket.uniqueBookings++
                }

                dayCursor = dayCursor.plus({ days: 1 })
            }

            if (!isMaintenanceOrBlocked) {
                if (dateRangeDailyHash[bookingCheckInKey] && !dayOfPayouts[booking.source]) {
                    const checkInDate = timeService.createLuxonDate(new Date(booking.checkIn))
                    const isEndOfMonth = checkInDate.day === checkInDate.daysInMonth
                    const payoutDateString = isEndOfMonth
                        ? bookingCheckInKey
                        : checkInDate.plus({ days: 1 }).toFormat(DATE_FORMAT)

                    if (dateRangeDailyHash[payoutDateString]) {
                        const payoutMonthYear = `${payoutDateString.slice(0, 4)}-${payoutDateString.slice(5, 7)}`
                        mappedData[payoutMonthYear].payout += payoutEarnings
                    }
                }

                if (dayOfPayouts[booking.source] && dateRangeDailyHash[bookingCheckInKey]) {
                    const hashedDate = timeService.createLuxonDate(new Date(booking.dateBooked)).toFormat(DATE_FORMAT)
                    if (dateRangeDailyHash[hashedDate]) {
                        const payoutMonthYear = `${hashedDate.slice(0, 4)}-${hashedDate.slice(5, 7)}`
                        mappedData[payoutMonthYear].payout += payoutEarnings
                    }
                }
            }
        })

        Object.keys(dateRangeDailyHash).forEach((dayKey: string) => {
            const dayInfo = bookingsByDay[dayKey]
            if (!dayInfo) {
                return
            }

            const monthYear = `${dayKey.slice(0, 4)}-${dayKey.slice(5, 7)}`
            const bucket = mappedData[monthYear]
            if (dayInfo.earnings || dayInfo.booking?._id) {
                bucket.leadTimeNights++
            }
            if (dayInfo.leadTime) {
                bucket.leadTimeSum += dayInfo.leadTime
            }
        })

        Object.keys(dailyBookings).forEach((dayKey: string) => {
            if (dailyBookings[dayKey].length > 1) {
                propertyOverlaps.push({
                    date: dayKey,
                    bookings: dailyBookings[dayKey],
                })
            }
        })

        Object.keys(mappedData).forEach((monthYear: string) => {
            mappedData[monthYear].nightsRemaining = Math.max(
                0,
                (futureDaysByMonthYear[monthYear] || 0) - Object.keys(futureBookedDaysByMonthYear[monthYear] || {}).length
            )
        })

        Object.keys(mappedData).forEach(d => {
            const mData = mappedData[d]
            mData.adr = mData.earnings && mData.nights ? +(mData.earnings / mData.nights).toFixed(2) : 0
            mData.occupancy = mData.nights ? +((mData.nights / mData.days) * 100).toFixed(2) : 0
            mData.leadTime = mData.leadTimeNights ? Math.round(mData.leadTimeSum / mData.leadTimeNights) : 0
            mData.guests = mData.guests ? +((mData.guests / mData.uniqueBookings)).toFixed(2) : 0

            mData.uniqueBookings = mData.uniqueBookings ? mData.uniqueBookings : 0
            mData.stayLength = mData.nights && mData.activeBookings ? +(mData.nights / mData.activeBookings).toFixed(2) : 0

            mData.trueAdr = mData.earnings && mData.sleeps ? +(mData.earnings / mData.sleeps).toFixed(2) : 0
            mData.trueOccupancy = mData.trueOccupancySleeps ? +((mData.trueOccupancySleeps / mData.days) * 100).toFixed(2) : 0
            mData.trueStayLength = mData.sleeps && mData.activeBookings ? +(mData.sleeps / mData.activeBookings).toFixed(2) : 0
            mData.revpar = mData.days ? +(mData.earnings / mData.days).toFixed(2) : 0
            
        })

        const monthlySummary: any = {}
        const totals = emptyReportNumbers()
        const mappedDataLength = Object.keys(mappedData).length

        Object.keys(mappedData).forEach(d => {
            if (!dateRangeHash[d]) {
                return
            }
            monthlySummary[d] = mappedData[d]
            totals.earnings += mappedData[d].earnings
            totals.days += mappedData[d].days
            totals.nights += mappedData[d].nights
            totals.sleeps += mappedData[d].sleeps
            totals.trueOccupancySleeps += mappedData[d].trueOccupancySleeps
            totals.nightsRemaining += mappedData[d].nightsRemaining
            totals.leadTimeSum += mappedData[d].leadTimeSum
            totals.leadTimeNights += mappedData[d].leadTimeNights
            totals.guests += mappedData[d].guests / mappedDataLength
            totals.payout += mappedData[d].payout
            totals.uniqueBookings += mappedData[d].uniqueBookings
            totals.activeBookings += mappedData[d].activeBookings
        })

        totals.activeBookings = activeBookingsInRange
        totals.adr = totals.earnings && totals.nights ? +(totals.earnings / totals.nights).toFixed(2) : 0
        totals.occupancy = totals.nights ? +((totals.nights / totals.days) * 100).toFixed(2) : 0
        totals.leadTime = totals.leadTimeNights ? Math.round(totals.leadTimeSum / totals.leadTimeNights) : 0
        totals.guests = totals.guests ? +(totals.guests).toFixed(2) : 0
        totals.stayLength = totals.nights && totals.activeBookings ? +(totals.nights / totals.activeBookings).toFixed(2) : 0

        totals.trueAdr = totals.earnings && totals.sleeps ? +(totals.earnings / totals.sleeps).toFixed(2) : 0
        totals.trueOccupancy = totals.trueOccupancySleeps ? +((totals.trueOccupancySleeps / totals.days) * 100).toFixed(2) : 0
        totals.trueStayLength = totals.sleeps && totals.activeBookings ? +(totals.sleeps / totals.activeBookings).toFixed(2) : 0
        totals.revpar = totals.days ? +(totals.earnings / totals.days).toFixed(2) : 0
        

        propertyReport.push({
            propertyId,
            mappedData,
            shortTitle: myProperties && myProperties[propertyId] ? myProperties[propertyId].title.split(' - ')[0] : '-',
            monthlySummary,
            totals,
            overlaps: propertyOverlaps,
        })
    })

    return propertyReport.sort((a, b) => { return a.shortTitle > b.shortTitle ? 1 : -1 })
}

export const generatePropertyTotalsReport = (
    propertyReportData: any,
    myProperties: any,
    startDate: number,
    endDate: number,
) => {

    const dateRangeHash = dateHasher(startDate, endDate, { dateFormat: 'yyyy-MM' })

    const totalsByMonth: any = {}
    // Use internal accumulators to calculate per-month averages while excluding properties created after the month
    const monthAccumulators: any = {}

    Object.keys(dateRangeHash).forEach((d: string) => {
        totalsByMonth[d] = emptyReportNumbers()
        monthAccumulators[d] = {
            leadTimeSum: 0,
            leadTimeNights: 0,
            guestsSum: 0,
            guestsCount: 0,
        }
    })

    // Aggregate per-property monthly values, skipping months where the property did not yet exist
    propertyReportData.forEach((p: any) => {
        const propertyCreated = myProperties && myProperties[p.propertyId] ? new Date(myProperties[p.propertyId].created).getTime() : 0

        Object.keys(p.monthlySummary).forEach((d: string) => {
            if (!dateRangeHash[d]) {
                return
            }

            // compute month end for d (format yyyy-MM)
            const monthStart = timeService.createLuxonDate(`${d}-01`)
            const monthEnd = monthStart.endOf('month').endOf('day').toMillis()

            if (propertyCreated > monthEnd) {
                // property created after this month — exclude
                return
            }

            totalsByMonth[d].earnings += p.monthlySummary[d].earnings
            totalsByMonth[d].payout += p.monthlySummary[d].payout
            totalsByMonth[d].days += p.monthlySummary[d].days
            totalsByMonth[d].nights += p.monthlySummary[d].nights
            totalsByMonth[d].sleeps += p.monthlySummary[d].sleeps
            totalsByMonth[d].trueOccupancySleeps += p.monthlySummary[d].trueOccupancySleeps
            totalsByMonth[d].nightsRemaining += p.monthlySummary[d].nightsRemaining
            totalsByMonth[d].uniqueBookings += p.monthlySummary[d].uniqueBookings
            totalsByMonth[d].activeBookings += p.monthlySummary[d].activeBookings

            monthAccumulators[d].leadTimeSum += p.monthlySummary[d].leadTimeSum
            monthAccumulators[d].leadTimeNights += p.monthlySummary[d].leadTimeNights
            monthAccumulators[d].guestsSum += p.monthlySummary[d].guests
            monthAccumulators[d].guestsCount += 1
        })
    })

    // finalize per-month derived metrics
    Object.keys(totalsByMonth).forEach((d: string) => {
        const acc = monthAccumulators[d]
        const t = totalsByMonth[d]

        t.leadTime = acc.leadTimeNights ? Math.round(acc.leadTimeSum / acc.leadTimeNights) : 0
        t.leadTimeSum = acc.leadTimeSum
        t.leadTimeNights = acc.leadTimeNights
        t.guests = acc.guestsCount ? +(acc.guestsSum / acc.guestsCount).toFixed(2) : 0

        t.adr = t.earnings && t.nights ? +(t.earnings / t.nights).toFixed(2) : 0
        t.occupancy = t.nights && t.days ? +((t.nights / t.days) * 100).toFixed(2) : 0
        t.stayLength = t.nights && t.activeBookings ? +(t.nights / t.activeBookings).toFixed(2) : 0

        t.trueAdr = t.earnings && t.sleeps ? +(t.earnings / t.sleeps).toFixed(2) : 0
        t.trueOccupancy = t.trueOccupancySleeps && t.days ? +((t.trueOccupancySleeps / t.days) * 100).toFixed(2) : 0
        t.trueStayLength = t.sleeps && t.activeBookings ? +(t.sleeps / t.activeBookings).toFixed(2) : 0
        t.revpar = t.days ? +(t.earnings / t.days).toFixed(2) : 0
    })

    // Build totalsReport by summing across months (so months properly exclude not-yet-created properties)
    const totalsReport: any = emptyReportNumbers()
    Object.keys(totalsByMonth).forEach((d: string) => {
        const t = totalsByMonth[d]
        totalsReport.earnings += t.earnings
        totalsReport.payout += t.payout
        totalsReport.days += t.days
        totalsReport.nights += t.nights
        totalsReport.sleeps += t.sleeps
        totalsReport.trueOccupancySleeps += t.trueOccupancySleeps
        totalsReport.nightsRemaining += t.nightsRemaining
        totalsReport.uniqueBookings += t.uniqueBookings
        totalsReport.leadTimeSum += t.leadTimeSum
        totalsReport.leadTimeNights += t.leadTimeNights
    })

    // For leadTime and guests overall, compute average across properties that existed by endDate
    const includedPropertiesForRange = propertyReportData.filter((p: any) => {
        const propertyCreated = myProperties && myProperties[p.propertyId] ? new Date(myProperties[p.propertyId].created).getTime() : 0
        return propertyCreated <= endDate
    })
    totalsReport.leadTime = totalsReport.leadTimeNights ? Math.round(totalsReport.leadTimeSum / totalsReport.leadTimeNights) : 0
    totalsReport.guests = includedPropertiesForRange.length ? +(includedPropertiesForRange.reduce((sum: number, p: any) => sum + p.totals.guests, 0) / includedPropertiesForRange.length).toFixed(2) : 0
    totalsReport.activeBookings = includedPropertiesForRange.reduce((sum: number, p: any) => sum + p.totals.activeBookings, 0)
    totalsReport.stayLength = totalsReport.nights && totalsReport.activeBookings ? +(totalsReport.nights / totalsReport.activeBookings).toFixed(2) : 0

    totalsReport.adr = totalsReport.earnings && totalsReport.nights ? +(totalsReport.earnings / totalsReport.nights).toFixed(2) : 0
    totalsReport.occupancy = totalsReport.nights && totalsReport.days ? +((totalsReport.nights / totalsReport.days) * 100).toFixed(2) : 0
    totalsReport.trueAdr = totalsReport.earnings && totalsReport.sleeps ? +(totalsReport.earnings / totalsReport.sleeps).toFixed(2) : 0
    totalsReport.trueOccupancy = totalsReport.trueOccupancySleeps && totalsReport.days ? +((totalsReport.trueOccupancySleeps / totalsReport.days) * 100).toFixed(2) : 0
    totalsReport.trueStayLength = totalsReport.sleeps && totalsReport.activeBookings ? +(totalsReport.sleeps / totalsReport.activeBookings).toFixed(2) : 0
    totalsReport.revpar = totalsReport.days ? +(totalsReport.earnings / totalsReport.days).toFixed(2) : 0

    return {
        totalsReport,
        totalsByMonth,
    }
}

const emptyReportNumbers = () => ({
    nights: 0,
    sleeps: 0,
    trueOccupancySleeps: 0,
    nightsRemaining: 0,
    activeBookings: 0,
    leadTimeSum: 0,
    leadTimeNights: 0,
    days: 0,
    earnings: 0,
    payout: 0,
    leadTime: 0,
    occupancy: 0,
    adr: 0,
    revpar: 0,
    guests: 0,
    uniqueBookings: 0,
    stayLength: 0,

    trueOccupancy: 0,
    trueAdr: 0,
    trueStayLength: 0,

})

export const TABLE_MODE_BUTTONS = [
    { label: 'Earnings', value: 'earnings', },
    { label: 'Payout', value: 'payout', },

    { label: 'ADR', value: 'adr', },
    { label: '*ADR', value: 'trueAdr', advanced: true },

    { label: 'RevPAR', value: 'revpar', },

    { label: 'Occupancy', value: 'occupancy', },
    { label: '*Occupancy', value: 'trueOccupancy', advanced: true },

    { label: 'Lead Time', value: 'leadTime', },
    { label: 'Guests', value: 'guests', },

    { label: 'Stay Length', value: 'stayLength', },
    { label: '*Stay Length', value: 'trueStayLength', advanced: true },

    { label: 'Nights Remaining', value: 'nightsRemaining', },

    { label: 'Sleeps', value: 'sleeps', },

    { label: 'Unique Bookings', value: 'uniqueBookings', },
]

export const DATE_FILTER_FORM: FieldGeneratorFieldType[] = [
    {
        fieldName: 'dateRange',
        fieldLabel: 'Date Range',
        fieldType: 'dateRange',
        placeholder: 'mm/dd/yyyy - mm/dd/yyyy',
        extra: {
            startDate: 'checkIn',
            endDate: 'checkOut',
        },
        layout: {
            xs: 12,
            md: 6,
        },
    },
    {
        fieldName: 'checkIn',
        fieldLabel: 'Check In',
        fieldType: 'date',
        required: true,
        hidden: true,
    },
    {
        fieldName: 'checkOut',
        fieldLabel: 'Check Out',
        fieldType: 'date',
        required: true,
        hidden: true,
    },
    {
        fieldName: 'asOf',
        fieldLabel: 'As of Date',
        fieldType: 'date',
        layout: {
            xs: 12,
            md: 6,
        },
    }
]

export const numberFormatter = (value: number, type: string) => {
    const trimmedValue = +(value.toFixed(2))
    const formatTypes: any = {
        'adr': (v: number) => currencyFormatter(v),
        'occupancy': (v: number) => `${v}%`,
        'leadTime': (v: number) => `${v} days`,
        'earnings': (v: number) => currencyFormatter(v),
        'payout': (v: number) => currencyFormatter(v),
        'guests': (v: number) => `${v} guests`,
        'stayLength': (v: number) => `${v} nights`,
        'uniqueBookings': (v: number) => `${v} bookings`,

        'nightsRemaining': (v: number) => `${v} nights`,

        'sleeps': (v: number) => `${v} sleeps`,

        'trueAdr': (v: number) => currencyFormatter(v),
        'revpar': (v: number) => currencyFormatter(v),
        'trueOccupancy': (v: number) => `${v}%`,
        'trueStayLength': (v: number) => `${v} nights`,
    }

    if (formatTypes[type]) {
        return formatTypes[type](trimmedValue)
    }

    return trimmedValue
}

export const saveSelectedPropertiesToLocal = (selectedProperties: any) => {
    localStorage.setItem('selectedProperties', JSON.stringify(selectedProperties))
}

export const getSelectedPropertiesFromLocal = () => {
    try {
        const selectedProperties = localStorage.getItem('selectedProperties')
        return selectedProperties ? JSON.parse(selectedProperties) : {}
    } catch (err) {
        return {}
    }
}